/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import type { StandardResponse } from '@musedrops/shared';

export type { StandardResponse };

export interface EdgeResponseHandlerOptions {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  notify?: (message: string, options?: { type: 'error' }) => void;
  logger?: Pick<typeof console, 'error' | 'log'>;
}

function parseJsonString(raw: string): unknown {
  if (!raw || typeof raw !== 'string') return raw;

  try {
    return JSON.parse(raw);
  } catch {
    let text = raw.trim();

    text = text.replace(/```(?:json)?\s*([\s\S]*?)\s*```/gi, '$1');
    text = text.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
    text = text.replace(/,(\s*[}\]])/g, '$1');

    try {
      return JSON.parse(text);
    } catch {
      return raw;
    }
  }
}

function resolveOptions(
  notifyOrOptions?:
    | EdgeResponseHandlerOptions['notify']
    | EdgeResponseHandlerOptions
): EdgeResponseHandlerOptions {
  if (!notifyOrOptions) return { logger: console };
  if (typeof notifyOrOptions === 'function') {
    return { notify: notifyOrOptions, logger: console };
  }
  return { logger: console, ...notifyOrOptions };
}

export function handleEdgeFunctionResponse<T>(
  data: StandardResponse<T>,
  notifyOrOptions?:
    | EdgeResponseHandlerOptions['notify']
    | EdgeResponseHandlerOptions
): T | null {
  const { notify, logger = console } = resolveOptions(notifyOrOptions);

  if (data.success === false) {
    const errorMessage = data.error || 'Operation failed';
    logger.error('Edge function error:', errorMessage);
    if (notify) {
      notify(errorMessage, { type: 'error' });
    }
    return null;
  }

  if (data.success !== true) {
    logger.error('Invalid response - missing or invalid success field:', data);
    if (notify) {
      notify('Invalid response from server', { type: 'error' });
    }
    return null;
  }

  if (data.result === undefined || data.result === null) {
    logger.error('Invalid response - missing result field:', data);
    if (notify) {
      notify('Invalid response format from server', { type: 'error' });
    }
    return null;
  }

  let { result } = data;
  if (typeof result === 'string') {
    result = parseJsonString(result) as T;
  }

  logger.log('🎯 [responseHandler] Final result sent to UI:', result);
  return result;
}
